import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import {
  chaveClassificacoesPersonalizadas,
  rotaClassificacoesPersonalizadas,
  useClassificacoesPersonalizadas,
} from '@/lib/classificacao-personalizada';
import { request } from '@/lib/http';
import {
  type ClassificacaoPersonalizada,
  type ClassificacaoPersonalizadaEntrada,
  classificacaoPersonalizadaSchema,
} from '@fiscoai/contracts';

import { RECONFERENCIA_MS } from '../regras/api';

export const useClassificacoes = useClassificacoesPersonalizadas;

/** Toda escrita reconfere as notas da empresa: relê agora e depois da reconferência. */
function useAoGravar(empresaId: string) {
  const cliente = useQueryClient();
  return () => {
    const reler = () => {
      void cliente.invalidateQueries({ queryKey: chaveClassificacoesPersonalizadas(empresaId) });
      void cliente.invalidateQueries({ queryKey: ['regras-classificacao', empresaId] });
      void cliente.invalidateQueries({ queryKey: ['documentos'], refetchType: 'none' });
      void cliente.invalidateQueries({ queryKey: ['pendencias'] });
    };
    reler();
    setTimeout(reler, RECONFERENCIA_MS);
  };
}

export function useSalvarClassificacao(empresaId: string) {
  const aoGravar = useAoGravar(empresaId);
  const base = rotaClassificacoesPersonalizadas(empresaId);
  return useMutation({
    mutationFn: ({ id, ...entrada }: ClassificacaoPersonalizadaEntrada & { id: string | null }) =>
      request(id ? `${base}/${id}` : base, {
        method: id ? 'PUT' : 'POST',
        body: entrada,
        schema: classificacaoPersonalizadaSchema,
      }),
    onSuccess: aoGravar,
  });
}

export function useApagarClassificacao(empresaId: string) {
  const aoGravar = useAoGravar(empresaId);
  return useMutation({
    mutationFn: (id: string) =>
      request(`${rotaClassificacoesPersonalizadas(empresaId)}/${id}`, {
        method: 'DELETE',
        schema: z.unknown(),
      }),
    onSuccess: aoGravar,
  });
}

/** A classificação como corpo de escrita (para ativar/desativar sem abrir o editor). */
export function entradaDaClassificacao(
  c: ClassificacaoPersonalizada,
): ClassificacaoPersonalizadaEntrada {
  return {
    nome: c.nome,
    ativa: c.ativa,
    cfopEntrada: c.cfopEntrada,
    grupo: c.grupo,
    patrimonio: c.patrimonio,
  };
}
